/**
 * TypeScript Types & Interfaces for Checkout New
 * Centralized type definitions untuk semua checkout components
 */

// Import EcommerceItem from existing tracking module (don't re-export to avoid duplication)
import type { EcommerceItem } from '../tracking/build-ecommerce-items';

// Export formatters, validation, and error handling functions
export * from './checkout-error-handler';
export * from './formatters';
export * from './promo-display';
export * from './validation';

// ============================================================================
// Form Data Types
// ============================================================================

export interface CheckoutFormData {
  delivery_date: string;
  delivery_option: string;
  delivery_note: string;
  payment_method: string;
  ship_to_contact: ShipToContact;
  purchase_order_document?: File[];
  items: OrderItem[];
  saved_total?: number;
  total?: number;
  transaction_id?: string;
}

export interface ShipToContact {
  name: string;
  telp: string;
}

// ============================================================================
// Order Data Types
// ============================================================================

export interface OrderItem {
  products: Product[];
  warehouse_id: string;
  warehouse_name: string;
}

export interface Product {
  id: string;
  item_code: string;
  title: string;
  brand_name: string;
  brand_desc?: string;
  category_name?: string;
  sub_category_name?: string;
  type?: string;
  quantity: number;
  price: number;
  total_price?: number;
  product_image?: string;
  discount_amount?: number;
  discount_promo_code?: string;
  has_promo?: boolean;
  promo_info?: PromoInfo[];
}

export interface PromoInfo {
  type: 'discount' | 'cashback' | 'free-item';
  text: string;
  amount?: number;
  item?: FreeGoodsItem;
}

export interface FreeGoodsItem {
  title: string;
  quantity: number;
  image?: string;
}

// ============================================================================
// Shipping Types
// ============================================================================

export interface ShipmentDates {
  dates_available: string[];
  dates_available_ui: string[];
}

export interface DeliveryOption {
  value: string;
  label: string;
  description?: string;
}

export interface ShippingAddress {
  warehouse_id: string;
  warehouse_name: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  province: string;
  postal_code: string;
  full_address: string;
}

// ============================================================================
// Payment Types
// ============================================================================

export interface PaymentMethodOption {
  value: string;
  label: string;
  description: string;
  icon?: string;
  badge?: string;
  isNew?: boolean;
  disabled?: boolean;
}

export interface PaymentSummary {
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  saved_total?: number;
}

// ============================================================================
// Validation Types
// ============================================================================

export interface OrderValidationResult {
  code: string;
  message: string;
  data: ProductChangeData[];
  invalid_promos?: InvalidPromo[];
}

export interface ProductChangeData {
  id: string;
  item_code: string;
  title: string;
  brand_desc: string;
  product_image: string;
  price: number;
  price_diff?: number;
  stock: number;
  stock_diff?: number;
  is_price_diff: boolean;
  is_stock_diff: boolean;
}

export interface InvalidPromo {
  promo_id: string;
  promo_type: 'discount' | 'cashback' | 'free_goods';
  reason: string;
  affected_products: string[];
}

// ============================================================================
// Modal Types
// ============================================================================

/**
 * Warning type constants to avoid magic strings
 */
export const WARNING_TYPES = {
  NO_PRODUCT: 'no_product',
  PRICE_STOCK_CHANGES: 'price_stock_changes',
  MIN_ORDER: 'min_order',
  MIN_ORDER_TOTAL_DIFF: 'min_order_total_diff',
  PROMO_INVALID: 'promo_invalid',
  VOUCHER_INVALID: 'voucher_invalid',
} as const;

export type WarningType =
  | 'no_product'
  | 'price_stock_changes'
  | 'min_order'
  | 'min_order_total_diff'
  | 'promo_invalid'
  | 'voucher_invalid'
  | null;

/**
 * Invalid voucher data for validation modal
 */
export interface InvalidVoucherData {
  id: string;
  code: string;
}

export interface ValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  warningType: WarningType;
  warningMessage: string | null;
  productChanges: ProductChangeData[];
  invalidPromos?: any; // ProcessedPromo type dari existing
  onReturnToCart: () => void;
  onContinueOrder: () => void;
  isLoading?: boolean;
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface ShippingSectionProps {
  shipmentDates?: ShipmentDates;
  onDeliveryChange?: (shippingTier: string, deliveryOption: string) => void;
}

export interface DocumentSectionProps {
  onRemoveFile: (index: number) => void;
  uploadedFiles?: File[];
}

export interface ProductSectionProps {
  items?: OrderItem[];
}

export interface PaymentSectionProps {
  selectedPaymentMethod?: string;
  onPaymentChange?: (method: string) => void;
}

export interface SummarySectionProps {
  summary?: PaymentSummary;
  isSubmitting?: boolean;
  isDisabled?: boolean;
  onSubmit?: () => void;
}

// ============================================================================
// Utility Types
// ============================================================================

export interface ApiError {
  error: string;
  message: string;
  data?: any;
}

export interface UploadFileResponse {
  urls: string[];
}

export interface SubmitOrderPayload {
  id: string;
  warehouse: string;
  sap_voucher_code?: string;
}

// ============================================================================
// Tracking Event Types
// ============================================================================

export interface PurchaseEventData {
  transaction_id?: string;
  currency: string;
  value: number;
  total_discount?: number;
  items: EcommerceItem[];
  section_name: string;
}

export interface ShippingInfoEventData {
  currency: string;
  value: number;
  shipping_tier: string;
  delivery_option: string;
  warehouse: string;
  meta_id: string;
  items: EcommerceItem[];
  section_name: string;
}

export interface PaymentInfoEventData {
  currency: string;
  value: number;
  payment_type: string;
  warehouse: string;
  meta_id: string;
  items: EcommerceItem[];
  section_name: string;
}
